/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Suspense } from 'react';
import { Await } from 'react-router';
import type { Image } from '@/types';
import { AttributeDefinition, Component, RegionDefinition } from '@/lib/decorators';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { DynamicImage } from '@/components/dynamic-image';
import { Link } from '@/components/link';
import ProductPrice from '@/components/product-price';
import { createProductUrl } from '@/lib/product/product-utils';
import { cn } from '@/lib/utils';
import type { EditorialProductCarouselLoaderData } from './loaders';

// oxlint-disable-next-line react/only-export-components
export { loader } from './loaders';

/* v8 ignore start */
@Component('editorialProductCarousel', {
    name: 'Editorial Product Carousel',
    description:
        'Editorial copy paired with up to 10 horizontally scrollable products using authored imagery and live catalog names and prices.',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class EditorialProductCarouselMetadata {
    @AttributeDefinition({
        id: 'eyebrow',
        name: 'Eyebrow',
        type: 'string',
        defaultValue: 'NO SMALL ROLES. NO SMALL BAGS.',
    })
    eyebrow?: string;

    @AttributeDefinition({
        id: 'heading',
        name: 'Heading',
        type: 'string',
        defaultValue: 'MEET COCO X BAGGALLINI',
    })
    heading?: string;

    @AttributeDefinition({
        id: 'bodyCopy',
        name: 'Body Copy',
        type: 'text',
        defaultValue:
            "Inspired by the rhythm of Coco's real life — from backstage to business meetings, from travel to family life. Six bags. Three colorways, hand-picked by Coco. Every piece built with the silhouette, space, and organization to handle it all.",
    })
    bodyCopy?: string;

    @AttributeDefinition({
        id: 'ctaText',
        name: 'CTA Text',
        type: 'string',
        defaultValue: 'SHOP EXCLUSIVE CAPSULE COLLECTION',
    })
    ctaText?: string;

    @AttributeDefinition({ id: 'ctaLink', name: 'CTA Link', type: 'url' })
    ctaLink?: string;

    @AttributeDefinition({ id: 'product1Image', name: 'Product 1 Image', type: 'image' })
    product1Image?: Image;
    @AttributeDefinition({ id: 'product1Id', name: 'Product 1', type: 'product' })
    product1Id?: string;

    @AttributeDefinition({ id: 'product2Image', name: 'Product 2 Image', type: 'image' })
    product2Image?: Image;
    @AttributeDefinition({ id: 'product2Id', name: 'Product 2', type: 'product' })
    product2Id?: string;

    @AttributeDefinition({ id: 'product3Image', name: 'Product 3 Image', type: 'image' })
    product3Image?: Image;
    @AttributeDefinition({ id: 'product3Id', name: 'Product 3', type: 'product' })
    product3Id?: string;

    @AttributeDefinition({ id: 'product4Image', name: 'Product 4 Image', type: 'image' })
    product4Image?: Image;
    @AttributeDefinition({ id: 'product4Id', name: 'Product 4', type: 'product' })
    product4Id?: string;

    @AttributeDefinition({ id: 'product5Image', name: 'Product 5 Image', type: 'image' })
    product5Image?: Image;
    @AttributeDefinition({ id: 'product5Id', name: 'Product 5', type: 'product' })
    product5Id?: string;

    @AttributeDefinition({ id: 'product6Image', name: 'Product 6 Image', type: 'image' })
    product6Image?: Image;
    @AttributeDefinition({ id: 'product6Id', name: 'Product 6', type: 'product' })
    product6Id?: string;

    @AttributeDefinition({ id: 'product7Image', name: 'Product 7 Image', type: 'image' })
    product7Image?: Image;
    @AttributeDefinition({ id: 'product7Id', name: 'Product 7', type: 'product' })
    product7Id?: string;

    @AttributeDefinition({ id: 'product8Image', name: 'Product 8 Image', type: 'image' })
    product8Image?: Image;
    @AttributeDefinition({ id: 'product8Id', name: 'Product 8', type: 'product' })
    product8Id?: string;

    @AttributeDefinition({ id: 'product9Image', name: 'Product 9 Image', type: 'image' })
    product9Image?: Image;
    @AttributeDefinition({ id: 'product9Id', name: 'Product 9', type: 'product' })
    product9Id?: string;

    @AttributeDefinition({ id: 'product10Image', name: 'Product 10 Image', type: 'image' })
    product10Image?: Image;
    @AttributeDefinition({ id: 'product10Id', name: 'Product 10', type: 'product' })
    product10Id?: string;
}
/* v8 ignore stop */

type ProductReference = string | { id?: string };

export type EditorialProductCarouselData = EditorialProductCarouselLoaderData;

export interface EditorialProductCarouselProps {
    eyebrow?: string;
    heading?: string;
    /** Optional presentation override for wrappers that reuse this component. */
    headingClassName?: string;
    /** Optional product-image motion override for wrappers that reuse this component. */
    productImageHoverClassName?: string;
    // RGBARRY-CUSTOMIZATION: reusable carousel wrappers can opt into visible controls while retaining swipe/drag.
    showNavigationControls?: boolean;
    // END RGBARRY-CUSTOMIZATION
    bodyCopy?: string;
    ctaText?: string;
    ctaLink?: string;
    product1Image?: Image;
    product1Id?: ProductReference;
    product2Image?: Image;
    product2Id?: ProductReference;
    product3Image?: Image;
    product3Id?: ProductReference;
    product4Image?: Image;
    product4Id?: ProductReference;
    product5Image?: Image;
    product5Id?: ProductReference;
    product6Image?: Image;
    product6Id?: ProductReference;
    product7Image?: Image;
    product7Id?: ProductReference;
    product8Image?: Image;
    product8Id?: ProductReference;
    product9Image?: Image;
    product9Id?: ProductReference;
    product10Image?: Image;
    product10Id?: ProductReference;
    data?: EditorialProductCarouselData | Promise<EditorialProductCarouselData> | null;
    /** Collapse the editorial column and expand the product row when no copy or CTA is authored. */
    collapseEditorialWhenEmpty?: boolean;
}

const normalizeProductId = (reference: ProductReference | undefined): string | undefined => {
    const id = typeof reference === 'string' ? reference : reference?.id;
    const trimmed = id?.trim();
    return trimmed || undefined;
};

const getImage = (props: EditorialProductCarouselProps, slot: number): Image | undefined =>
    props[`product${slot}Image` as keyof EditorialProductCarouselProps] as Image | undefined;

const getProductReference = (props: EditorialProductCarouselProps, slot: number): ProductReference | undefined =>
    props[`product${slot}Id` as keyof EditorialProductCarouselProps] as ProductReference | undefined;

function ProductSlides({
    data,
    props,
    fullWidth = false,
}: {
    data: EditorialProductCarouselData | null;
    props: EditorialProductCarouselProps;
    fullWidth?: boolean;
}) {
    const items = (data?.items ?? []).flatMap(({ slot, product }) => {
        const image = getImage(props, slot);
        const configuredProductId = normalizeProductId(getProductReference(props, slot));
        return image?.url && configuredProductId ? [{ slot, image, product }] : [];
    });

    if (!items.length) return null;

    return (
        <Carousel
            opts={{ align: 'start', dragFree: true, containScroll: 'trimSnaps' }}
            className="w-full"
            aria-label="Featured products">
            {/* RGBARRY-CUSTOMIZATION: make the swipe/drag affordance explicit for the Custom Image Carousel. */}
            <CarouselContent
                className={cn(
                    'ml-0 gap-5 pr-6 sm:gap-7 lg:gap-10 lg:pr-10',
                    props.showNavigationControls && 'touch-pan-y cursor-grab select-none active:cursor-grabbing'
                )}>
                {items.map(({ slot, image, product }) => {
                    const productName = product.name ?? '';
                    const productUrl = createProductUrl(product.id, null, 'color', null, productName);
                    return (
                        <CarouselItem
                            key={`${slot}-${product.id}`}
                            className={cn(
                                'basis-[78%] pl-0 sm:basis-[48%]',
                                fullWidth
                                    ? 'lg:basis-[30%] xl:basis-[23%] 2xl:basis-[calc((100%_-_12.5rem)/6)]'
                                    : 'lg:basis-[45%] xl:basis-[38%] 2xl:basis-[34%]'
                            )}>
                            <Link to={productUrl} className="group block text-center">
                                <div className="relative aspect-[5/7] overflow-hidden bg-muted">
                                    <DynamicImage
                                        src={image.url}
                                        alt={productName}
                                        widths={{ base: 300, sm: 360, lg: 420 }}
                                        className="h-full w-full [&_picture]:block [&_picture]:h-full [&_picture]:w-full"
                                        imageProps={{
                                            className: cn(
                                                'h-full w-full object-cover transition-transform duration-500 ease-out motion-reduce:transition-none group-hover:scale-[1.02] motion-reduce:group-hover:scale-100',
                                                props.productImageHoverClassName
                                            ),
                                        }}
                                    />
                                </div>
                                <h3 className="mt-5 text-lg font-normal leading-6 text-foreground">{productName}</h3>
                                <ProductPrice
                                    product={product}
                                    currency={data?.currency ?? 'USD'}
                                    labelForA11y={productName}
                                    currentPriceOnly
                                    hidePromo
                                    className="mt-3 justify-center text-lg font-bold"
                                    currentPriceProps={{ className: 'text-lg font-bold' }}
                                />
                            </Link>
                        </CarouselItem>
                    );
                })}
            </CarouselContent>
            {props.showNavigationControls && (
                <>
                    <CarouselPrevious className="left-3 z-10 hidden size-10 bg-background/90 shadow-md sm:flex" />
                    <CarouselNext className="right-3 z-10 hidden size-10 bg-background/90 shadow-md sm:flex" />
                </>
            )}
            {/* END RGBARRY-CUSTOMIZATION */}
        </Carousel>
    );
}

// oxlint-disable-next-line react/only-export-components
export function fallback() {
    return (
        <div className="grid min-h-[32rem] grid-cols-2 gap-6 px-6 py-12" aria-hidden="true">
            <div className="animate-pulse bg-muted" />
            <div className="animate-pulse bg-muted" />
        </div>
    );
}

export default function EditorialProductCarousel(props: EditorialProductCarouselProps) {
    const {
        eyebrow,
        heading,
        headingClassName,
        bodyCopy,
        ctaText,
        ctaLink,
        data,
        collapseEditorialWhenEmpty = false,
    } = props;
    // RGBARRY-CUSTOMIZATION: Custom Image Carousel can become a product-only full-width row when
    // no editorial fields are authored, while the existing Editorial Product Carousel stays unchanged.
    const hasEditorialContent = Boolean(eyebrow || heading || bodyCopy || (ctaText && ctaLink));
    const useProductOnlyLayout = collapseEditorialWhenEmpty && !hasEditorialContent;
    // END RGBARRY-CUSTOMIZATION

    return (
        <section className="overflow-hidden bg-background py-12 lg:py-16">
            <div
                className={cn(
                    'grid items-center gap-10',
                    useProductOnlyLayout ? 'grid-cols-1 lg:gap-0' : 'lg:grid-cols-[42vw_minmax(0,1fr)] lg:gap-0'
                )}>
                {!useProductOnlyLayout && (
                    <div className="px-6 sm:px-10 lg:pl-[max(3rem,calc((100vw-90rem)/2+4.5rem))] lg:pr-16">
                        {eyebrow && (
                            <p className="text-sm font-medium uppercase tracking-[0.12em] text-foreground lg:text-base">
                                {eyebrow}
                            </p>
                        )}
                        {heading && (
                            <h2
                                className={cn(
                                    'mt-5 text-3xl font-bold uppercase leading-[1.05] tracking-[-0.04em] text-foreground lg:text-[2.5rem]',
                                    headingClassName
                                )}>
                                {heading}
                            </h2>
                        )}
                        {bodyCopy && (
                            <p className="mt-5 max-w-[31rem] text-base leading-6 text-foreground lg:text-lg lg:leading-7">
                                {bodyCopy}
                            </p>
                        )}
                        {ctaText && ctaLink && (
                            <Button
                                asChild
                                variant="outline"
                                className="mt-10 h-auto min-h-12 rounded-none border-foreground px-6 py-3 text-sm font-semibold uppercase tracking-[0.12em] sm:px-8">
                                <Link to={ctaLink}>{ctaText}</Link>
                            </Button>
                        )}
                    </div>
                )}

                <div
                    className={cn(
                        'min-w-0',
                        useProductOnlyLayout ? 'section-container w-full px-6 sm:px-10 lg:px-8' : 'pl-6 lg:pl-0'
                    )}>
                    <Suspense fallback={fallback()}>
                        <Await resolve={data ?? null} errorElement={null}>
                            {(resolvedData) => (
                                <ProductSlides data={resolvedData} props={props} fullWidth={useProductOnlyLayout} />
                            )}
                        </Await>
                    </Suspense>
                </div>
            </div>
        </section>
    );
}
